// Summary: Pure KDS page helpers for grouping, sorting, and ticket presentation state.
// Details: docs/function-library/web/contexts-pages.md#pages-kdspage
import type { KitchenStatusValue, KdsOrderView, KdsOrderlineView, KdsOrderPrintPreviewPayload } from '../../../services/kds/kds.service';
import type { KdsLanguage } from '../i18n/kds-i18n';
import type { KdsPrintTicketLine } from '../components/print/KdsPrintTicket';
import { compareCategoryNamesForDisplay } from '../../../services/categories/category-order.util';
import { groupOrderlineSelections } from '../../../utils/orderline-selections';

// Summary: KDS page copy, pure helpers, and constants kept out of the TSX (Rule 32).
export const KDS_PAGE_TITLE = 'מסך מטבח';
export type { KdsLanguage };

export const KITCHEN_STATUS_LABELS: Record<KitchenStatusValue | 'null', string> = {
  null: 'ממתין',
  COOKING: 'בהכנה',
  READY: 'מוכן',
  DONE: 'הוגש',
  HIDE: 'מוסתר',
};

export const KDS_URGENT_NOTE_TAG = 'דחוף';

// Architectural summary: Strips the tenant-name prefix from orderCode (format: {sessionName}/{seq}) so the KDS card only shows the session/order identifier. If the code has 3+ parts the first part is the tenant prefix and is stripped. Changes here affect the order code label displayed on every KDS ticket.
export function stripOrderCode(orderCode: string): string {
  const parts = orderCode.split('/');
  return parts.length >= 3 ? parts.slice(1).join('/') : orderCode;
}

// Architectural summary: Formats elapsed time since order creation for the KDS card age badge.
export function formatElapsedTime(createdAt: string, language: KdsLanguage = 'he'): string {
  const elapsed = Date.now() - new Date(createdAt).getTime();
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return language === 'en' ? 'Now' : 'עכשיו';
  if (minutes < 60) return language === 'en' ? `${minutes}m` : `${minutes} ד׳`;
  const hours = Math.floor(minutes / 60);
  return language === 'en' ? `${hours}h` : `${hours} ש׳`;
}

// Architectural summary: Returns the next kitchen status in the COOKING -> READY -> DONE -> null cycle triggered by a kitchen screen tap. The cycle is: null -> COOKING -> READY -> DONE -> null (reset). Changes here affect the status transition UX on every orderline badge across all kitchen displays.
export function nextKitchenStatus(current: KitchenStatusValue | null): KitchenStatusValue | null {
  if (current === null) return 'COOKING';
  if (current === 'COOKING') return 'READY';
  if (current === 'READY') return 'DONE';
  return null;
}

// Architectural summary: Returns the display label for a kitchen status value, including null.
export function getStatusLabel(status: KitchenStatusValue | null): string {
  return KITCHEN_STATUS_LABELS[status ?? 'null'];
}


export const KDS_ZOOM_KEY = 'kds-zoom';


export type KdsOrderSort = 'duration' | 'product_count' | 'table';

export const KDS_ORDER_SORT_KEY = 'kds-order-sort';
export const KDS_LANGUAGE_KEY = 'kds-language';
export const KDS_ORDER_TYPE_FILTER_KEY = 'kds-order-type-filter';
export type KdsOrderTypeFilter = 'all' | 'dine_in' | 'take_away';

// Architectural summary: Returns true if any orderline on the order has a note containing
// 'דחוף' or 'urgent' (case-insensitive). Used to float urgent tickets just below VIP in the KDS sort.
export function isUrgentOrder(order: KdsOrderView): boolean {
  return order.orderlines.some((line) => {
    if (!line.note) return false;
    const lower = line.note.toLowerCase();
    return lower.includes('דחוף') || lower.includes('urgent');
  });
}
export const KDS_SHOW_DONE_TAB_KEY = 'kds-show-done-tab';
export const KDS_MERGE_ORDERS_KEY = 'kds-merge-orders';

// Architectural summary: Sorts a KDS order list according to the user-selected sort mode. 'duration' = ascending min(orderline.createdAt) - the oldest kitchen line, not order.createdAt - so the sort matches the elapsed-time badge on each card (which also uses the oldest line); 'product_count' = descending orderlines.length (busiest orders surface first); 'table' = ascending tableNumber with Hebrew-numeric locale collation. Does not handle favorites-pin; that secondary sort is applied by the caller on top of this result. Changes here affect the ticket sequence in every KDS grid view and the corresponding sort tests in kds-page.unit.spec.ts.
export function sortOrders(orders: KdsOrderView[], sort: KdsOrderSort): KdsOrderView[] {
  const sorted = [...orders];
  if (sort === 'duration') {
    sorted.sort((a, b) => {
      const aTime = a.orderlines.length > 0
        ? Math.min(...a.orderlines.map((l) => new Date(l.createdAt).getTime()))
        : new Date(a.createdAt).getTime();
      const bTime = b.orderlines.length > 0
        ? Math.min(...b.orderlines.map((l) => new Date(l.createdAt).getTime()))
        : new Date(b.createdAt).getTime();
      return aTime - bTime;
    });
  } else if (sort === 'product_count') {
    sorted.sort((a, b) => b.orderlines.length - a.orderlines.length);
  } else {
    sorted.sort((a, b) => (a.tableNumber ?? a.orderCode).localeCompare(b.tableNumber ?? b.orderCode, 'he', { numeric: true }));
  }
  // Priority pre-pass: VIP first, then urgent (note contains 'דחוף'/'urgent'), then regular.
  return [
    ...sorted.filter((order) => order.isVip),
    ...sorted.filter((order) => !order.isVip && isUrgentOrder(order)),
    ...sorted.filter((order) => !order.isVip && !isUrgentOrder(order)),
  ];
}


// Architectural summary: Returns the localStorage key for the per-tenant kitchen printer selection.
export const kdsKitchenPrinterKey = (tenantId: string) => `kds-kitchen-printer:${tenantId}`;


export type KdsTab = 'COOKING' | 'READY' | 'DONE';

export type KdsTabCard = {
  cardId: string;
  displayOrder: KdsOrderView;
  sourceOrder: KdsOrderView;
  isPartial: boolean;
};

export type KdsOverviewLine = {
  productName: string;
  quantity: number;
};

export type KdsOverviewGroup = {
  categoryId: number | null;
  categoryName: string | null;
  lines: KdsOverviewLine[];
};

// 'all' = no filter; number = a category; 'uncategorized' = lines with no category.
export type KdsCategoryFilter = number | 'all' | 'uncategorized';

export type KdsCategoryChip = { id: number; name: string };

export type KdsPrintPreview = {
  previewId: string | null;
  orderId: number;
  orderNumber: string;
  tableNumber: string;
  isTakeAway: boolean;
  isTableless: boolean;
  orderCode: string;
  dinersCount: number;
  orderlines: KdsPrintTicketLine[];
  printerName: string;
  printTime: Date;
  waiterName: string | null;
};


export const DARK_MODE_KEY = 'kds-dark-mode';
export const KDS_NOTIFICATION_SOUND_KEY = 'kds-notification-sound';
export const favoritesKey = (tenantId: string) => `kds-favorites:${tenantId}`;


export function isLineCooking(line: KdsOrderlineView): boolean {
  return line.kitchenStatus === null || line.kitchenStatus === 'COOKING';
}

export function isLineReady(line: KdsOrderlineView): boolean {
  return line.kitchenStatus === 'READY';
}

export function isLineDone(line: KdsOrderlineView): boolean {
  return line.kitchenStatus === 'DONE';
}

export function isLineCancelled(line: KdsOrderlineView): boolean {
  return line.status === 'canceled';
}


export type KdsLine = KdsOrderlineView & {
  lineKind: 'orderline' | 'selection';
  selectionId?: number;
  parentOrderlineId: number;
};

export function expandOrderLines(order: KdsOrderView): KdsLine[] {
  const lines: KdsLine[] = [];
  for (const ol of order.orderlines) {
    if (ol.kitchenStatus === 'HIDE') continue;
    const selections = ol.selections ?? [];
    const hasCombo = selections.some((s) => s.groupType === 'combo');
    if (!hasCombo) {
      lines.push({ ...ol, lineKind: 'orderline', parentOrderlineId: ol.id });
      continue;
    }
    const variantByParent = new Map<number, typeof selections[number]>();
    for (const s of selections) {
      if (s.groupType === 'variant' && s.parentItemPk != null) variantByParent.set(s.parentItemPk, s);
    }
    for (const s of selections) {
      if (s.groupType !== 'combo') continue; // variants never become their own row
      if (s.kds !== true) continue; // shows only when the combo item's KDS is explicitly ON (off/unset = hidden)
      if (s.kitchenStatus === 'HIDE') continue;
      const nested = s.itemPk != null ? variantByParent.get(s.itemPk) : undefined;
      const label = nested ? nested.itemName : s.itemName;
      lines.push({
        ...ol,
        id: s.id,
        productName: s.quantity > 1 ? `${label} ×${s.quantity}` : label,
        quantity: ol.quantity,
        kitchenStatus: s.kitchenStatus,
        wasPulledBack: s.wasPulledBack,
        wasReprinted: s.wasReprinted,
        createdAt: s.createdAt,
        updatedAt: s.updatedAt,
        selectionLabels: [],
        selections: [],
        lineKind: 'selection',
        selectionId: s.id,
        parentOrderlineId: ol.id,
      });
    }
  }
  return lines;
}

export function splitLineIds(lines: KdsLine[]): { orderlineIds: number[]; selectionIds: number[] } {
  const orderlineIds: number[] = [];
  const selectionIds: number[] = [];
  for (const line of lines) {
    if (line.lineKind === 'selection' && line.selectionId != null) {
      selectionIds.push(line.selectionId);
    } else {
      orderlineIds.push(line.parentOrderlineId);
    }
  }
  return { orderlineIds, selectionIds };
}


// Architectural summary: Assigns each line a stable "fire wave" identity, frozen the first
// time the line is observed cooking, so its ticket grouping survives later status changes
// (COOKING -> READY -> DONE) instead of drifting whenever updatedAt is bumped by a status
// write. A pulled-back line always gets a brand-new key (never reuses its previous frozen
// key), matching the "pull-back starts a new standalone ticket" behavior in buildTabCards.
// Pure function: returns a new Map, never mutates previousKeys. Caller (useKdsController)
// is responsible for persisting the returned map across renders (useRef) and feeding it back
// in as previousKeys on the next call.
// The send-to-kitchen fire action batch-updates every fired orderline's own updated_at in
// one shared UPDATE, but never touches orderline_variant_combo_selections rows — those keep
// whatever updated_at they got at creation (a separate, earlier insert). So a combo
// selection-line's OWN updatedAt never matches a plain sibling line's updatedAt from the
// same fire; key selection lines off their PARENT orderline's updatedAt instead, which the
// fire action does stamp consistently across the whole batch. Callers build this map once per
// order (O(orderlines)) rather than looking up the parent per line (O(lines × orderlines)).
function buildParentUpdatedAtById(order: KdsOrderView): Map<number, string> {
  return new Map(order.orderlines.map((ol) => [ol.id, ol.updatedAt]));
}

function waveTimestampOf(line: KdsLine, parentUpdatedAtById: Map<number, string>): string {
  if (line.lineKind !== 'selection') return line.updatedAt;
  return parentUpdatedAtById.get(line.parentOrderlineId) ?? line.updatedAt;
}

export function computeFireWaveKeys(
  orders: KdsOrderView[],
  previousKeys: Map<number, string>,
): Map<number, string> {
  const next = new Map(previousKeys);
  for (const order of orders) {
    const parentUpdatedAtById = buildParentUpdatedAtById(order);
    for (const line of expandOrderLines(order)) {
      if (isLineCancelled(line)) continue;
      const waveTimestamp = waveTimestampOf(line, parentUpdatedAtById);
      if (line.wasPulledBack) {
        next.set(line.id, `pulled-back:${line.id}:${line.updatedAt}`);
        continue;
      }
      // A reprint restarts this line's cooking session and must be treated as a fresh fire,
      // grouping every reprinted line from the SAME batch onto one shared new ticket — NOT its
      // own standalone ticket per line (that's pull-back's behavior, not reprint's). Key by the
      // shared waveTimestamp only (no line.id in the key), so two lines reprinted together land
      // on the same wave, while an untouched sibling (old frozen key) is correctly left out.
      // Always overwrite (never `if (next.has(...)) continue`), since a reprint must always
      // start a new wave even if this line already had an old frozen key from its original fire.
      if (line.wasReprinted) {
        next.set(line.id, `reprinted:${waveTimestamp}`);
        continue;
      }
      if (!isLineCooking(line)) continue;
      if (next.has(line.id)) continue;
      next.set(line.id, waveTimestamp);
    }
  }
  return next;
}

const NO_WAVE_KEY = '__no-wave__';

// Architectural summary: Builds per-tab card buckets. Each orderline's status is independent.
// When mergeOrders is false, fireWaveKeys (see computeFireWaveKeys) groups every active line
// by its frozen fire-wave identity FIRST, then buckets each wave's lines into COOKING/READY/
// DONE cards — so a wave started as two separate COOKING tickets stays two separate tickets
// as its lines individually progress to READY/DONE, instead of re-merging into one order-wide
// card the moment a line leaves COOKING.
export function buildTabCards(
  orders: KdsOrderView[],
  mergeOrders: boolean = true,
  fireWaveKeys: Map<number, string> = new Map(),
): Record<KdsTab, KdsTabCard[]> {
  const tabCards: Record<KdsTab, KdsTabCard[]> = {
    COOKING: [],
    READY: [],
    DONE: [],
  };

  for (const order of orders) {
    let cardIndex = 0;
    const nextCardId = () => `${order.id}:${cardIndex++}`;

    const allLines = expandOrderLines(order);
    if (allLines.length === 0) continue;
    const cancelledLines = allLines.filter(isLineCancelled);
    const activeLines = allLines.filter((l) => !isLineCancelled(l));
    if (activeLines.length === 0) continue;

    const pushCard = (
      tab: KdsTab,
      lines: KdsLine[],
      isPartial: boolean,
      note?: (line: KdsLine) => string,
    ) => {
      if (lines.length === 0) return;
      const displayLines = note ? lines.map((l) => ({ ...l, note: note(l) })) : lines;
      tabCards[tab].push({
        cardId: nextCardId(),
        displayOrder: { ...order, orderlines: [...displayLines, ...cancelledLines] },
        sourceOrder: order,
        isPartial,
      });
    };

    if (mergeOrders) {
      const hasCookingLines = activeLines.some(isLineCooking);
      const readyLines = activeLines.filter(isLineReady);
      const visibleDoneLines = activeLines.filter(isLineDone);
      const isAllReady = activeLines.length > 0 && activeLines.every(isLineReady);
      const isAllDone = activeLines.length > 0 && activeLines.every(isLineDone);

      if (isAllDone) {
        pushCard('DONE', visibleDoneLines, false);
        continue;
      }

      if (isAllReady) {
        pushCard('READY', activeLines, false);
      } else if (readyLines.length > 0) {
        pushCard('READY', readyLines, hasCookingLines);
      }

      if (hasCookingLines) {
        const cookingLines = activeLines.filter(isLineCooking);
        const sessionStartAt = (l: KdsOrderlineView): string =>
          l.wasPulledBack ? l.updatedAt : l.createdAt;
        const earliestCookingSessionStart = cookingLines.reduce(
          (min, l) => {
            const start = sessionStartAt(l);
            return min === '' || start < min ? start : min;
          },
          '',
        );
        const cookingDisplayLines = activeLines.filter((l) => {
          if (isLineCooking(l)) return true;
          if (isLineDone(l)) return false;
          return l.updatedAt >= earliestCookingSessionStart;
        });
        pushCard(
          'COOKING',
          cookingDisplayLines,
          activeLines.some((line) => isLineReady(line) || isLineDone(line)),
        );
      }

      if (visibleDoneLines.length > 0 && !isAllDone) {
        const hasCookingOrReady = hasCookingLines || readyLines.length > 0;
        pushCard('DONE', visibleDoneLines, hasCookingOrReady);
      }
      continue;
    }

    // mergeOrders === false: group by frozen fire-wave key first, then bucket per wave.
    const tagUrgent = (note: string | null | undefined): string => {
      const trimmed = (note ?? '').trim();
      if (trimmed.length === 0) return KDS_URGENT_NOTE_TAG;
      if (trimmed.includes(KDS_URGENT_NOTE_TAG)) return trimmed;
      return `${trimmed} ${KDS_URGENT_NOTE_TAG}`;
    };

    const parentUpdatedAtById = buildParentUpdatedAtById(order);
    const waveGroups = new Map<string, KdsLine[]>();
    for (const line of activeLines) {
      const waveTimestamp = waveTimestampOf(line, parentUpdatedAtById);
      // A pulled-back line always gets its own standalone wave (one ticket PER line), even if
      // the caller's fireWaveKeys map hasn't been recomputed since the pull-back yet.
      // A reprinted line instead joins a SHARED wave with every other line reprinted in the
      // same batch (one ticket for the whole batch, like a fresh fire) — keyed by timestamp
      // only, no line.id, so untouched siblings from the old wave are correctly excluded.
      // line.wasPulledBack/wasReprinted are the live source of truth here; the map is only
      // consulted when neither applies.
      const key = line.wasPulledBack
        ? `pulled-back:${line.id}:${line.updatedAt}`
        : line.wasReprinted
          ? `reprinted:${waveTimestamp}`
          : (fireWaveKeys.get(line.id) ?? NO_WAVE_KEY);
      const group = waveGroups.get(key) ?? [];
      group.push(line);
      waveGroups.set(key, group);
    }

    for (const [waveKey, waveLines] of waveGroups) {
      const isPulledBackWave = waveKey.startsWith('pulled-back:');
      const waveCooking = waveLines.filter(isLineCooking);
      const waveReady = waveLines.filter(isLineReady);
      const waveDone = waveLines.filter(isLineDone);
      const waveHasMultipleStatuses =
        [waveCooking.length > 0, waveReady.length > 0, waveDone.length > 0].filter(Boolean).length > 1;

      if (waveCooking.length > 0) {
        // While any line in this wave is still cooking, its already-READY sibling lines
        // ride along on the COOKING card too (shown checked/green), matching how a physical
        // kitchen ticket stays on the rail with completed items checked off until the whole
        // ticket clears — they also render on their own READY card below, simultaneously.
        // Preserve original line order (don't shuffle a READY line to the end).
        const cookingCardLines = waveLines.filter((l) => isLineCooking(l) || isLineReady(l));
        pushCard(
          'COOKING',
          cookingCardLines,
          isPulledBackWave || waveHasMultipleStatuses,
          isPulledBackWave ? (l) => tagUrgent(l.note) : undefined,
        );
      }
      if (waveReady.length > 0) {
        pushCard('READY', waveReady, waveHasMultipleStatuses);
      }
      if (waveDone.length > 0) {
        pushCard('DONE', waveDone, waveHasMultipleStatuses);
      }
    }
  }

  return tabCards;
}

// Architectural summary: Overview grouped by category (A-Z, uncategorized last) for active tab.
export function buildOverviewGroups(cards: KdsTabCard[], tab: KdsTab): KdsOverviewGroup[] {
  // categoryId key ('none' for uncategorized) -> { name, productName -> qty }
  const groups = new Map<string, { categoryId: number | null; categoryName: string | null; totals: Map<string, number> }>();
  for (const card of cards) {
    for (const line of card.displayOrder.orderlines) {
      if (isLineCancelled(line)) continue;
      if (tab === 'COOKING' && !isLineCooking(line)) continue;
      const key = line.categoryId != null ? String(line.categoryId) : 'none';
      let group = groups.get(key);
      if (!group) {
        group = { categoryId: line.categoryId ?? null, categoryName: line.categoryName ?? null, totals: new Map() };
        groups.set(key, group);
      }
      group.totals.set(line.productName, (group.totals.get(line.productName) ?? 0) + line.quantity);
    }
  }
  return Array.from(groups.values())
    .map((group) => ({
      categoryId: group.categoryId,
      categoryName: group.categoryName,
      lines: Array.from(group.totals.entries())
        .map(([productName, quantity]) => ({ productName, quantity }))
        .sort((a, b) => a.productName.localeCompare(b.productName, 'he')),
    }))
    .sort((a, b) => compareCategoryNamesForDisplay(a.categoryName, b.categoryName));
}

// Architectural summary: true when a KDS line matches the active category chip filter.
export function lineMatchesCategory(line: KdsOrderlineView, filter: KdsCategoryFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'uncategorized') return line.categoryId == null;
  return line.categoryId === filter;
}

// Architectural summary: Active categories in the tab + uncategorized flag (for filter chips).
export function collectKdsCategories(cards: KdsTabCard[]): { categories: KdsCategoryChip[]; hasUncategorized: boolean } {
  const byId = new Map<number, string>();
  let hasUncategorized = false;
  for (const card of cards) {
    for (const line of card.displayOrder.orderlines) {
      if (line.categoryId != null && line.categoryName != null) {
        byId.set(line.categoryId, line.categoryName);
      } else {
        hasUncategorized = true;
      }
    }
  }
  const categories = Array.from(byId.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => compareCategoryNamesForDisplay(a.name, b.name));
  return { categories, hasUncategorized };
}

// Architectural summary: Returns true when an order matches the search query against table number,
// order code, or product names.
export function matchesSearch(order: KdsOrderView, query: string): boolean {
  const q = query.toLowerCase();
  return (
    (order.tableNumber ?? '').toLowerCase().includes(q) ||
    order.orderCode.toLowerCase().includes(q) ||
    stripOrderCode(order.orderCode).toLowerCase().includes(q) ||
    order.orderlines.some((l) => l.productName.toLowerCase().includes(q))
  );
}

// Architectural summary: Reads the favorited/pinned order ID list for this tenant from
export function readFavorites(tenantId: string): Set<number> {
  try {
    const raw = localStorage.getItem(favoritesKey(tenantId));
    return new Set<number>(raw ? (JSON.parse(raw) as number[]) : []);
  } catch {
    return new Set();
  }
}


// Architectural summary: Normalizes a KDS card payload into the exact print-preview ticket shape
export function buildKdsPrintPreview(order: KdsOrderView, printerName: string, printTime: Date): KdsPrintPreview {
  const orderNumber = order.orderCode
    .split('/')
    .slice(-2)
    .map((part) => part.match(/\d+/)?.[0] ?? part)
    .join('/');
  return {
    previewId: null,
    orderId: order.id,
    orderNumber,
    tableNumber: order.tableNumber ?? order.orderCode,
    isTakeAway: order.orderType === 'take_away',
    isTableless: order.tableId == null,
    orderCode: order.orderCode,
    dinersCount: order.dinersCount,
    orderlines: expandOrderLines(order).filter((line) => !isLineCancelled(line)).map((line) => ({
      id: line.lineKind === 'selection' && line.selectionId != null ? line.selectionId : -line.parentOrderlineId,
      productName: line.productName,
      selectionGroups: groupOrderlineSelections(line.selections),
      quantity: line.quantity,
    })),
    printerName,
    printTime,
    waiterName: order.waiterName ?? null,
  };
}

// Architectural summary: Converts backend-generated preview artifact payload into local preview
export function buildKdsPrintPreviewFromBackend(previewId: string, payload: KdsOrderPrintPreviewPayload, order: KdsOrderView): KdsPrintPreview {
  return {
    previewId,
    orderId: payload.orderId,
    orderNumber: payload.orderNumber,
    tableNumber: payload.tableNumber || order.orderCode,
    isTakeAway: order.orderType === 'take_away',
    isTableless: order.tableId == null,
    orderCode: order.orderCode,
    dinersCount: payload.dinersCount,
    orderlines: payload.orderlines,
    printerName: payload.printerName,
    printTime: new Date(payload.printTime),
    waiterName: payload.waiterName,
  };
}


export async function loadKdsPrintFont(): Promise<void> {
  const linkId = 'kds-receipt-font';
  let link = document.getElementById(linkId) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Noto+Sans+Hebrew:wght@400;500;600;700;800&display=swap';
    await new Promise<void>((res) => {
      link!.addEventListener('load', () => res(), { once: true });
      link!.addEventListener('error', () => res(), { once: true });
      document.head.appendChild(link!);
    });
  }
  await Promise.all([
    document.fonts.load('400 1em "Noto Sans Hebrew"'),
    document.fonts.load('500 1em "Noto Sans Hebrew"'),
    document.fonts.load('700 1em "Noto Sans Hebrew"'),
    document.fonts.load('800 1em "Noto Sans Hebrew"'),
  ]);
}

const waitForPaint = (): Promise<void> => new Promise((resolve) => {
  requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
});

const wait = (milliseconds: number): Promise<void> => new Promise((resolve) => {
  setTimeout(resolve, milliseconds);
});

// Architectural summary: Captures the visible receipt DOM node into a high-quality JPEG data URL
export async function captureKdsReceiptSnapshot(receiptPaper: HTMLDivElement | null): Promise<string | undefined> {
  if (!receiptPaper) return undefined;
  if (process.env.NODE_ENV === 'test') return undefined;

  // React commits the hidden ticket asynchronously. Two animation frames ensure it has a
  // measured layout before html2canvas clones it; this is especially important on slow devices.
  await waitForPaint();

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const bounds = receiptPaper.getBoundingClientRect();
      if (bounds.width <= 0 || bounds.height <= 0) {
        throw new Error('Print ticket has no rendered dimensions');
      }

      await loadKdsPrintFont();
      const module = await import('html2canvas');
      const html2canvas = module.default;
      const canvas = await html2canvas(receiptPaper, {
        backgroundColor: '#ffffff',
        scale: 3,
        useCORS: true,
        logging: false,
        imageTimeout: 5000,
        onclone: (clonedDocument) => {
          // html2canvas renders inside a cloned iframe. Keep this ticket in that clone's viewport
          // so browser layout optimizations cannot discard the source node when it is off-screen.
          const clonedPaper = clonedDocument.querySelector<HTMLElement>('[data-kds-print-capture="true"]');
          if (clonedPaper) {
            clonedPaper.style.position = 'fixed';
            clonedPaper.style.top = '0';
            clonedPaper.style.left = '0';
            clonedPaper.style.transform = 'none';
            clonedPaper.style.visibility = 'visible';
            clonedPaper.style.opacity = '1';
          }
        },
      });
      if (canvas.width <= 0 || canvas.height <= 0) {
        throw new Error('Print ticket capture produced an empty canvas');
      }

      const image = canvas.toDataURL('image/jpeg', 0.98);
      if (image.length > 1000) {
        return image;
      }
      throw new Error('Print ticket capture produced an empty image');
    } catch (error) {
      if (attempt === 2) {
        console.warn('KDS print ticket capture failed after retries', error);
        return undefined;
      }
      await wait(150 * (attempt + 1));
      await waitForPaint();
    }
  }

  return undefined;
}
